/**
 * Copyright 2026, IntraLinks, Inc. All rights reserved.
 */

import React, {
  ChangeEvent, FormEvent, useEffect, useRef, useState
} from 'react';
import {
  IlButton, IlInlineLoading, IlTextInput, IlInlineNotification, IlLoading
} from 'il-framework-component';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
import { RootState } from '../../../../app/store';
import { DEFAULT_EMAIL_DIGITS, I18nNamespaces, LOA_RESEND_SECONDS } from '../../../../app/shared/utils/constants.util';
import { AuthErrorKey, SecuritySetupType } from '../../../../domain/models/impl/SecuritySetup';
import { useRequiredLevel, maskEmailFixed, getLoaValidationMessageKey } from '../../../../app/shared/utils/helpers.util';
import usePreserveSearchNav from '../../../../app/shared/hooks/usePreserveSearchNav';
import {
  setError,
  setValidationError,
  enrollEmailFactor,
  challengeFactor,
  verifyOrActivateFactor,
  resetEnrollmentState
} from '../../setupSecuritySlice';

import { SecuritySetupRoutes } from '../../../../app/shared/utils/securitySetupRoutes';
import styles from './EnrollEmail.scss';

const EnrollEmail: React.FC = () => {
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const dispatch = useDispatch();
  const requiredLevel = useRequiredLevel();
  const { goBackOrReplace } = usePreserveSearchNav();

  const actionLoading = useSelector((s: RootState) => s.setupSecurity.actionLoading);
  const validationError = useSelector((s: RootState) => s.setupSecurity.validationError);
  const emailErrorKey = useSelector((s: RootState) => s.setupSecurity.error);
  const selectedFactor = useSelector((s: RootState) => s.setupSecurity.selectedFactor);

  const [code, setCode] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const initOnceRef = useRef(false);

  const factorId = selectedFactor?.factorId ?? '';
  const digits = selectedFactor?.digits ?? DEFAULT_EMAIL_DIGITS;
  const typedDigits = code.replace(/\D/g, '').length;
  const canSubmit = !!factorId && digits > 0 && !actionLoading && !validationError && typedDigits === digits;
  const canResend = !!factorId && resendIn === 0 && !actionLoading;
  const profileEmail = useSelector((s: RootState) => s.profile.profileDetails?.externalId);
  const emailText = maskEmailFixed(profileEmail) || t('loaEnrichment.factors.email.fallbackEmail');
  const isEmailFactor = selectedFactor?.type === SecuritySetupType.EMAIL;

  const isInitLoading = actionLoading && (!isEmailFactor || !factorId);

  const inlineTitleMap: Record<string, string> = {
    [AuthErrorKey.DefaultEmailErrorMessage]: t(
      'loaEnrichment.factors.email.inline.startFailed',
      { ns: I18nNamespaces.LOA_ENRICHMENT }
    ),
    [AuthErrorKey.DefaultChallengeError]: t(
      'loaEnrichment.common.errors.challengeFailedInline',
      { ns: I18nNamespaces.LOA_ENRICHMENT }
    )
  };

  const inlineTitle = inlineTitleMap[emailErrorKey]
    ?? t('loaEnrichment.factors.email.errors.defaultEmailInlineNotification', { ns: I18nNamespaces.LOA_ENRICHMENT });

  useEffect(() => () => {
    dispatch(resetEnrollmentState());
  }, [dispatch]);

  useEffect(() => {
    if (selectedFactor && !isEmailFactor) {
      goBackOrReplace(SecuritySetupRoutes.root);
      return;
    }

    if (!selectedFactor && !initOnceRef.current) {
      initOnceRef.current = true;

      (async () => {
        const newFactorId = await dispatch(enrollEmailFactor(t, { loaLevel: requiredLevel }) as any);
        if (newFactorId) {
          setResendIn(LOA_RESEND_SECONDS);
        } else {
          initOnceRef.current = false;
        }
      })();
    }
  }, [dispatch, selectedFactor, isEmailFactor, goBackOrReplace, requiredLevel, t]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setInterval(() => setResendIn((prev) => Math.max(0, prev - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const clearErrors = () => {
    dispatch(setError(''));
    dispatch(setValidationError(''));
  };

  const updateCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors();
    const input = e.currentTarget.value.replace(/\D/g, '');
    setCode(input.slice(0, digits));
  };

  const sendCode = async () => {
    if (!factorId) return;
    const shouldStartCooldown = await dispatch(challengeFactor(t, {
      factorId,
      factorType: SecuritySetupType.EMAIL,
      toastKey: 'loaEnrichment.common.errors.sendFailedToast',
      errorKey: AuthErrorKey.DefaultChallengeError
    }) as any);
    if (shouldStartCooldown) setResendIn(LOA_RESEND_SECONDS);
  };

  const submit = (ev: FormEvent | ChangeEvent) => {
    ev.preventDefault();
    if (!factorId) return;

    dispatch(verifyOrActivateFactor(t, {
      factorId,
      code: code.replace(/\s/g, ''),
      action: 'activate',
      successToastMsg: 'loaEnrichment.common.messages.activated',
      elevateLevel: selectedFactor?.loaLevel ?? requiredLevel
    }) as any);
  };

  if (isInitLoading) {
    return <IlLoading withOverlay />;
  }

  return (
    <>
      <h1 className={styles.formTitle}>{t('loaEnrichment.factors.email.title')}</h1>
      <p className={styles.helpText}>{t('loaEnrichment.factors.email.verifyHelpText', { email: emailText })}</p>
      {emailErrorKey && (
        <div className={styles.inlineNotification}>
          <IlInlineNotification
            title={inlineTitle}
            notificationType='inline'
            kind='error'
            hideCloseButton
          />
        </div>
      )}

      <form onSubmit={submit}>
        <IlTextInput
          id='email-activation-code'
          labelText={t('loaEnrichment.common.labels.verificationCode')}
          placeholder={t('loaEnrichment.common.placeholders.code', { digits })}
          type='text'
          maxLength={digits}
          invalid={!!validationError}
          invalidText={t(getLoaValidationMessageKey(validationError) ?? 'loaEnrichment.common.errors.default')}
          value={code}
          onChange={updateCode}
          autoFocus
          autoComplete='off'
        />

        <div className={styles.activationContainer}>
          <div className={styles.resendContainer}>
            <p className={styles.resendText}>{t('loaEnrichment.common.resend.emailPrompt')}</p>
            <IlButton
              className={styles.resendBtn}
              kind='ghost'
              type='button'
              disabled={!canResend}
              onClick={sendCode}
            >
              {resendIn > 0 ? t('loaEnrichment.common.resend.resendIn', { seconds: resendIn })
                : t('loaEnrichment.common.buttons.resendCode')}
            </IlButton>
          </div>

          <IlButton kind='primary' type='submit' disabled={!canSubmit}>
            {t('loaEnrichment.common.buttons.activate')}
          </IlButton>
        </div>
      </form>

      {actionLoading && factorId && (
        <div className={styles.loadingContainer}>
          <IlInlineLoading description={t('loaEnrichment.common.status.pleaseWait')} success={false} onSuccess={noop} />
        </div>
      )}
    </>
  );
};

export default EnrollEmail;
